import api from '@/services/utils/apiInstance.ts';
import { displayNonNotFoundError } from '@/utils/apiErrorUtils.ts';

import type { CustomAxiosRequestConfig } from '@/types/services/utils/axios.type.ts';
import type { LcbftClassification, LcbftClassificationResponseApi } from '@/types/services/lcbft/lcbft.type.ts';

function mapClassification(data: LcbftClassificationResponseApi): LcbftClassification {
  return {
    id: data.id,
    userId: data.user_id,
    classification: data.classification,
    totalScore: data.total_score,
    isPep: data.is_pep,
    isSanctioned: data.is_sanctioned,
    breakdown: data.breakdown,
    missingData: data.missing_data,
    warnings: data.warnings ?? [],
    computedAt: data.computed_at
  }
}

class LcbftService {
  // Returns `null` if no classification has been computed yet (404) — this is a normal empty
  // state for this client, not an error. Other errors are surfaced via a toast and also resolve
  // to `null` so the rest of the "fiche client" page can still render.
  async getClassification(userId: number): Promise<LcbftClassification | null> {
    try {
      const response = await api.get(
        `/lcbft-classification/bo/${userId}`,
        { disableGlobalError: true } as CustomAxiosRequestConfig
      )
      return mapClassification(response.data)
    } catch (error) {
      displayNonNotFoundError(error)
      return null
    }
  }

  // Triggers a fresh computation (first computation or recompute) from currently available data
  // (client questionnaire, KYC, etc). Unlike `getClassification`, errors here are real errors —
  // left to the global axios interceptor's default toast handling.
  async recompute(userId: number): Promise<LcbftClassification> {
    const response = await api.post(`/lcbft-classification/bo/${userId}/recompute`)
    return mapClassification(response.data)
  }
}

export default new LcbftService();
